import React, { useMemo } from 'react';
import { Zap, Leaf, Info } from 'lucide-react';
import TimerWidget from './TimerWidget';

const toText = (v) => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') return v.tip || v.text || v.content || v.optimization || v.suggestion || '';
  return String(v ?? '');
};

const OptimizationItem = ({ opt }) => {
  if (typeof opt === 'string') return <span>{opt}</span>;
  if (opt && typeof opt === 'object') {
    const { note, ingredient, substitution, tip, text, content } = opt;
    if (substitution || ingredient) {
      return (
        <span className="flex flex-col gap-0.5">
          {ingredient && (
            <span>
              <span className="line-through text-green-400">{ingredient}</span>
              {substitution && (
                <>
                  <span className="mx-1.5 text-green-400">→</span>
                  <span className="font-medium text-green-900">{substitution}</span>
                </>
              )}
            </span>
          )}
          {!ingredient && substitution && <span className="font-medium text-green-900">{substitution}</span>}
          {note && <span className="text-xs text-green-600 italic">{note}</span>}
        </span>
      );
    }
    return <span>{tip || text || content || toText(opt)}</span>;
  }
  return <span>{toText(opt)}</span>;
};

const APPLIANCE_META = {
  airfryer: { label: 'Airfryer', icon: '🌀', color: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-400' },
  vapeur:   { label: 'Vapeur',   icon: '💨', color: 'bg-blue-100 text-blue-800 border-blue-200',     dot: 'bg-blue-400' },
  four:     { label: 'Four',     icon: '🔲', color: 'bg-red-100 text-red-800 border-red-200',         dot: 'bg-red-400' },
  poele:    { label: 'Poêle',    icon: '🍳', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-400' },
  classic:  { label: 'Classique',icon: '🍽', color: 'bg-gray-100 text-gray-700 border-gray-200',      dot: 'bg-gray-400' },
  thermomix:{ label: 'Thermomix',icon: '⚡', color: 'bg-purple-100 text-purple-800 border-purple-200', dot: 'bg-purple-400' },
};

const fallbackMeta = (appliance) => ({
  label: appliance,
  icon: '🍽',
  color: 'bg-gray-100 text-gray-700 border-gray-200',
  dot: 'bg-gray-400',
});

// Derive one timer per unique timed appliance from the steps
function deriveTimers(steps) {
  const map = new Map();
  for (const step of steps) {
    const duration = step.config?.time || step.duration_minutes;
    if (!duration || step.appliance === 'classic') continue;
    if (!map.has(step.appliance)) {
      map.set(step.appliance, { appliance: step.appliance, minutes: duration });
    } else {
      // keep the largest duration for that appliance
      const existing = map.get(step.appliance);
      if (duration > existing.minutes) existing.minutes = duration;
    }
  }
  return [...map.values()];
}

const ApplanceBadge = ({ appliance }) => {
  const meta = APPLIANCE_META[appliance] || fallbackMeta(appliance);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
      {meta.icon} {meta.label}
    </span>
  );
};

const TimelineStep = ({ step, index, totalSteps }) => {
  const isLast = index === totalSteps - 1;
  const meta = APPLIANCE_META[step.appliance] || fallbackMeta(step.appliance);
  const duration = step.config?.time || step.duration_minutes;
  const temp = step.config?.temp;

  return (
    <div className="flex gap-4">
      {/* Timeline line + dot */}
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 ${meta.dot}`} />
        {!isLast && <div className="w-0.5 bg-gray-200 flex-1 mt-1" />}
      </div>

      {/* Content */}
      <div className={`pb-5 ${isLast ? '' : ''} flex-1`}>
        {step.start_offset_minutes > 0 && (
          <p className="text-xs text-gray-400 mb-1">+{step.start_offset_minutes} min</p>
        )}
        <div className="flex flex-wrap items-start gap-2 mb-1.5">
          <ApplanceBadge appliance={step.appliance} />
          {temp && (
            <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium">
              🌡 {temp}°C
            </span>
          )}
          {duration && (
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              ⏱ {duration} min
            </span>
          )}
          {step.shaking_needed && (
            <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full">
              ⚡ Secouer
            </span>
          )}
        </div>
        <p className="text-sm text-gray-800 leading-relaxed">{step.text}</p>
      </div>
    </div>
  );
};

const RecipeExecutionView = ({ config }) => {
  const steps = useMemo(() => {
    if (!Array.isArray(config?.steps)) return [];
    return [...config.steps].sort((a, b) => (a.start_offset_minutes || 0) - (b.start_offset_minutes || 0));
  }, [config]);

  const timers = useMemo(() => deriveTimers(steps), [steps]);

  if (!config) return null;

  return (
    <div className="space-y-5">
      {/* Sync note */}
      {config.sync_note && (
        <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
          <Zap className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-800 font-medium">{config.sync_note}</p>
        </div>
      )}

      {/* Parallel timer dashboard */}
      {timers.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-3">
            Tableau de bord des minuteurs
          </p>
          <div className="flex flex-wrap gap-3">
            {timers.map(t => (
              <TimerWidget
                key={t.appliance}
                minutes={t.minutes}
                label={(APPLIANCE_META[t.appliance] || fallbackMeta(t.appliance)).icon + ' ' + (APPLIANCE_META[t.appliance] || fallbackMeta(t.appliance)).label}
                appliance={t.appliance}
                compact={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {steps.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-4">Chronologie</p>
          <div>
            {steps.map((step, i) => (
              <TimelineStep key={step.id || i} step={step} index={i} totalSteps={steps.length} />
            ))}
            {config.total_time_minutes && (
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-green-400 flex-shrink-0 mt-1" />
                </div>
                <p className="text-sm font-semibold text-green-700 pb-1">
                  🍽 Tout est prêt ! ({config.total_time_minutes} min)
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GI analysis */}
      {config.gi_analysis && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">{config.gi_analysis}</p>
        </div>
      )}

      {/* GI optimizations */}
      {Array.isArray(config.gi_optimizations) && config.gi_optimizations.length > 0 && (
        <div className="bg-green-50 rounded-2xl border border-green-100 p-5">
          <h3 className="font-semibold text-green-800 mb-3 text-sm flex items-center gap-2">
            <Leaf className="w-4 h-4" /> Optimisations IG bas
          </h3>
          <ul className="space-y-2">
            {config.gi_optimizations.map((opt, i) => (
              <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <OptimizationItem opt={opt} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RecipeExecutionView;
